import React, { useState, useEffect } from 'react';
import {
    Box,
    Paper,
    Typography,
    Button,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TablePagination,
    IconButton,
    Chip,
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Grid,
    Card,
    CardContent,
    Alert,
    CircularProgress,
    Tooltip,
} from '@mui/material';
import {
    Add as AddIcon,
    Edit as EditIcon,
    Delete as DeleteIcon,
    Visibility as ViewIcon,
    Search as SearchIcon,
    Clear as ClearIcon,
    FilterList as FilterIcon,
    CheckCircle as ActiveIcon,
    Cancel as InactiveIcon,
} from '@mui/icons-material';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../../store/store';
import {
    getRiders,
    getCities,
    deleteRider,
    toggleRiderStatus,
    setFilters,
    clearFilters,
    Rider,
    RiderFilters,
} from '../../store/slices/riderSlice';
import RiderForm from './RiderForm';
import RiderView from './RiderView';
import ConfirmationDialog from '../../components/ConfirmationDialog';
import GenericButton from '../../components/GenericButton';
import { useTheme } from '../../contexts/ThemeContext';

const Riders: React.FC = () => {
    const dispatch = useDispatch<AppDispatch>();
    const {
        riders,
        cities,
        pagination,
        loading,
        error,
        filters,
    } = useSelector((state: RootState) => state.riders);

    // Local state
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(20);
    const [searchQuery, setSearchQuery] = useState('');
    const [showFilters, setShowFilters] = useState(false);
    const [showForm, setShowForm] = useState(false);
    const [showView, setShowView] = useState(false);
    const [showDeleteDialog, setShowDeleteDialog] = useState(false);
    const [selectedRider, setSelectedRider] = useState<Rider | null>(null);
    const [deleteRiderId, setDeleteRiderId] = useState<number | null>(null);

    // Load data on component mount and when dependencies change
    useEffect(() => {
        dispatch(getRiders({ page: page + 1, per_page: rowsPerPage, filters }));
    }, [dispatch, page, rowsPerPage, filters]);

    useEffect(() => {
        dispatch(getCities());
    }, [dispatch]);

    const handlePageChange = (event: unknown, newPage: number) => {
        setPage(newPage);
    };

    const handleRowsPerPageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const handleSearch = () => {
        dispatch(setFilters({ query: searchQuery }));
        setPage(0);
    };

    const handleClearSearch = () => {
        setSearchQuery('');
        dispatch(setFilters({ query: undefined }));
        setPage(0);
    };

    const handleFilterChange = (filterType: keyof RiderFilters, value: any) => {
        dispatch(setFilters({ [filterType]: value }));
        setPage(0);
    };

    const handleClearFilters = () => {
        dispatch(clearFilters());
        setSearchQuery('');
        setPage(0);
    };

    const handleAddRider = () => {
        setSelectedRider(null);
        setShowForm(true);
    };

    const handleEditRider = (rider: Rider) => {
        setSelectedRider(rider);
        setShowForm(true);
    };

    const handleViewRider = (rider: Rider) => {
        setSelectedRider(rider);
        setShowView(true);
    };

    const handleDeleteRider = (rider: Rider) => {
        setDeleteRiderId(rider.id);
        setShowDeleteDialog(true);
    };

    const confirmDelete = async () => {
        if (deleteRiderId) {
            await dispatch(deleteRider(deleteRiderId));
            setShowDeleteDialog(false);
            setDeleteRiderId(null);
        }
    };

    const handleToggleStatus = async (rider: Rider) => {
        await dispatch(toggleRiderStatus(rider.id));
    };

    const handleFormClose = () => {
        setShowForm(false);
        setSelectedRider(null);
    };

    const handleViewClose = () => {
        setShowView(false);
        setSelectedRider(null);
    };

    const getStatusChip = (status: number) => {
        if (status === 1) {
            return <Chip label="Active" color="success" size="small" />;
        } else {
            return <Chip label="Inactive" color="error" size="small" />;
        }
    };

    const formatDate = (dateString: string) => {
        return new Date(dateString).toLocaleDateString();
    };

    return (
        <Box sx={{ p: 3 }}>
            {/* Header */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h4" component="h1" gutterBottom>
                    Riders
                </Typography>
                <GenericButton
                    variant="primary"
                    startIcon={<AddIcon />}
                    onClick={handleAddRider}
                    sx={{ minWidth: 120 }}
                >
                    Add Rider
                </GenericButton>
            </Box>

            {/* Error Alert */}
            {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                    {error}
                </Alert>
            )}

            {/* Search and Filters */}
            <Card sx={{ mb: 3 }}>
                <CardContent>
                    <Grid container spacing={2} alignItems="center">
                        <Grid size={{ xs: 12, md: 4 }}>
                            <TextField
                                fullWidth
                                label="Search riders"
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                                InputProps={{
                                    endAdornment: (
                                        <Box>
                                            {searchQuery && (
                                                <IconButton size="small" onClick={handleClearSearch}>
                                                    <ClearIcon />
                                                </IconButton>
                                            )}
                                            <IconButton size="small" onClick={handleSearch}>
                                                <SearchIcon />
                                            </IconButton>
                                        </Box>
                                    ),
                                }}
                            />
                        </Grid>
                        <Grid size={{ xs: 12, md: 2 }}>
                            <Button
                                variant="outlined"
                                startIcon={<FilterIcon />}
                                onClick={() => setShowFilters(!showFilters)}
                                fullWidth
                            >
                                Filters
                            </Button>
                        </Grid>
                        <Grid size={{ xs: 12, md: 2 }}>
                            <Button
                                variant="outlined"
                                onClick={handleClearFilters}
                                fullWidth
                            >
                                Clear All
                            </Button>
                        </Grid>
                    </Grid>

                    {/* Filter Options */}
                    {showFilters && (
                        <Box sx={{ mt: 2, pt: 2, borderTop: 1, borderColor: 'divider' }}>
                            <Grid container spacing={2}>
                                <Grid size={{ xs: 12, md: 3 }}>
                                    <FormControl fullWidth size="small">
                                        <InputLabel>City</InputLabel>
                                        <Select
                                            value={filters.city_id || ''}
                                            onChange={(e) => handleFilterChange('city_id', e.target.value || undefined)}
                                            label="City"
                                        >
                                            <MenuItem value="">All Cities</MenuItem>
                                            {cities.map((city) => (
                                                <MenuItem key={city.id} value={city.id}>
                                                    {city.name}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Grid>
                                <Grid size={{ xs: 12, md: 3 }}>
                                    <FormControl fullWidth size="small">
                                        <InputLabel>Status</InputLabel>
                                        <Select
                                            value={filters.status || ''}
                                            onChange={(e) => handleFilterChange('status', e.target.value || undefined)}
                                            label="Status"
                                        >
                                            <MenuItem value="">All Status</MenuItem>
                                            <MenuItem value="1">Active</MenuItem>
                                            <MenuItem value="0">Inactive</MenuItem>
                                        </Select>
                                    </FormControl>
                                </Grid>
                            </Grid>
                        </Box>
                    )}
                </CardContent>
            </Card>

            {/* Riders Table */}
            <Paper sx={{ width: '100%', overflow: 'hidden' }}>
                <TableContainer>
                    <Table stickyHeader>
                        <TableHead>
                            <TableRow>
                                <TableCell sx={{ fontWeight: 600 }}>Name</TableCell>
                                <TableCell sx={{ fontWeight: 600 }}>Email</TableCell>
                                <TableCell sx={{ fontWeight: 600 }}>Phone</TableCell>
                                <TableCell sx={{ fontWeight: 600 }}>City</TableCell>
                                <TableCell sx={{ fontWeight: 600 }}>PIN Code</TableCell>
                                <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                                <TableCell sx={{ fontWeight: 600 }}>Created</TableCell>
                                <TableCell sx={{ fontWeight: 600 }} align="center">Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {loading ? (
                                <TableRow>
                                    <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                                        <CircularProgress />
                                    </TableCell>
                                </TableRow>
                            ) : riders.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                                        <Typography variant="body2" color="text.secondary">
                                            No riders found
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            ) : (
                                riders.map((rider) => (
                                    <TableRow key={rider.id} hover>
                                        <TableCell>
                                            <Typography variant="body2" fontWeight={500}>
                                                {rider.name}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="body2">
                                                {rider.email}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="body2">
                                                {rider.profile?.phone || '-'}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="body2">
                                                {rider.profile?.city?.name || '-'}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="body2" fontFamily="monospace">
                                                {rider.rider?.pin_code || '-'}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            {getStatusChip(rider.status)}
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="body2">
                                                {formatDate(rider.created_at)}
                                            </Typography>
                                        </TableCell>
                                        <TableCell align="center">
                                            <Tooltip title="View Details">
                                                <IconButton
                                                    size="small"
                                                    onClick={() => handleViewRider(rider)}
                                                    color="primary"
                                                >
                                                    <ViewIcon />
                                                </IconButton>
                                            </Tooltip>
                                            <Tooltip title="Edit Rider">
                                                <IconButton
                                                    size="small"
                                                    onClick={() => handleEditRider(rider)}
                                                    color="primary"
                                                >
                                                    <EditIcon />
                                                </IconButton>
                                            </Tooltip>
                                            <Tooltip title={rider.status === 1 ? "Deactivate Rider" : "Activate Rider"}>
                                                <IconButton
                                                    size="small"
                                                    onClick={() => handleToggleStatus(rider)}
                                                    color={rider.status === 1 ? "error" : "success"}
                                                >
                                                    {rider.status === 1 ? <InactiveIcon /> : <ActiveIcon />}
                                                </IconButton>
                                            </Tooltip>
                                            <Tooltip title="Delete Rider">
                                                <IconButton
                                                    size="small"
                                                    onClick={() => handleDeleteRider(rider)}
                                                    color="error"
                                                >
                                                    <DeleteIcon />
                                                </IconButton>
                                            </Tooltip>
                                        </TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>

                {/* Pagination */}
                {pagination && (
                    <TablePagination
                        rowsPerPageOptions={[10, 20, 50]}
                        component="div"
                        count={pagination.total}
                        rowsPerPage={rowsPerPage}
                        page={page}
                        onPageChange={handlePageChange}
                        onRowsPerPageChange={handleRowsPerPageChange}
                        labelDisplayedRows={({ from, to, count }) =>
                            `${from}-${to} of ${count !== -1 ? count : `more than ${to}`}`
                        }
                    />
                )}
            </Paper>

            {/* Rider Form Modal */}
            <RiderForm
                open={showForm}
                rider={selectedRider}
                onClose={handleFormClose}
                onSuccess={() => {
                    handleFormClose();
                    dispatch(getRiders({ page: page + 1, per_page: rowsPerPage, filters }));
                }}
            />

            {/* Rider View Modal */}
            {selectedRider && (
                <RiderView
                    open={showView}
                    rider={selectedRider}
                    onClose={handleViewClose}
                />
            )}

            {/* Delete Confirmation Dialog */}
            <ConfirmationDialog
                open={showDeleteDialog}
                title="Delete Rider"
                message="Are you sure you want to delete this rider? This action cannot be undone."
                onConfirm={confirmDelete}
                onCancel={() => setShowDeleteDialog(false)}
            />
        </Box>
    );
};

export default Riders;
